import { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { VoiceInputButton } from "@/components/VoiceInputButton";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Highlighter,
  Undo,
  Redo,
  RemoveFormatting,
  Sparkles,
  Eraser,
  FileText,
  ChevronDown,
  Palette,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Table as TableIcon,
  Plus,
  Trash2,
  CheckSquare,
  ImageIcon,
  Code,
  Quote,
  SeparatorHorizontal,
} from 'lucide-react';
import { NOTE_TEMPLATES, TEXT_COLORS, HIGHLIGHT_COLORS } from './editorConstants';
import { ColorPickerButton } from './ColorPickerButton';

export const EditorToolbar = ({ editor, onCleanupHighlights, onAutoLink, onInsertTemplate, characterCount }) => {
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem("editorToolbarExpanded") === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("editorToolbarExpanded", String(expanded));
    } catch {
      // localStorage nicht verfügbar – Zustand bleibt nur für die Sitzung
    }
  }, [expanded]);

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('URL eingeben:');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt('Bild-URL eingeben:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const setTextColor = (color) => {
    editor.chain().focus().setColor(color).run();
  };

  const setHighlightColor = (color) => {
    editor.chain().focus().toggleHighlight({ color }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-muted/50 rounded-t-lg">
      {/* Templates Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-2 gap-1 text-primary hover:text-primary"
            title="Vorlage einfügen"
          >
            <FileText className="w-4 h-4" />
            <span className="text-xs hidden sm:inline">Vorlage</span>
            <ChevronDown className="w-3 h-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuLabel>Vorlage einfügen</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {NOTE_TEMPLATES.map((template) => {
            const Icon = template.icon;
            return (
              <DropdownMenuItem
                key={template.id}
                onClick={() => onInsertTemplate(template)}
                className="cursor-pointer"
              >
                <Icon className="w-4 h-4 mr-2" />
                {template.name}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="w-px h-8 bg-border mx-1" />

      {/* Headings */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={`h-8 px-2 ${editor.isActive('heading', { level: 1 }) ? 'bg-accent' : ''}`}
        title="Überschrift 1"
      >
        <Heading1 className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`h-8 px-2 ${editor.isActive('heading', { level: 2 }) ? 'bg-accent' : ''}`}
        title="Überschrift 2"
      >
        <Heading2 className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={`h-8 px-2 ${editor.isActive('heading', { level: 3 }) ? 'bg-accent' : ''}`}
        title="Überschrift 3"
      >
        <Heading3 className="w-4 h-4" />
      </Button>

      <div className="w-px h-8 bg-border mx-1" />

      {/* Basic formatting */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`h-8 px-2 ${editor.isActive('bold') ? 'bg-accent' : ''}`}
        title="Fett"
      >
        <Bold className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`h-8 px-2 ${editor.isActive('italic') ? 'bg-accent' : ''}`}
        title="Kursiv"
      >
        <Italic className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`h-8 px-2 ${editor.isActive('underline') ? 'bg-accent' : ''}`}
        title="Unterstrichen"
      >
        <UnderlineIcon className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={`h-8 px-2 ${editor.isActive('strike') ? 'bg-accent' : ''}`}
        title="Durchgestrichen"
      >
        <Strikethrough className="w-4 h-4" />
      </Button>

      <div className="w-px h-8 bg-border mx-1 hidden sm:block" />

      {/* Spracheingabe / Diktat */}
      <VoiceInputButton
        onTranscript={(text) => editor.chain().focus().insertContent(text + " ").run()}
        className="h-8 px-2"
        title="Diktieren (Spracheingabe)"
      />

      {/* Mobile expand/collapse toggle (nur auf dem Handy sichtbar) */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setExpanded((v) => !v)}
        className="h-8 px-2 gap-1 ml-auto sm:hidden"
        title={expanded ? "Weniger Werkzeuge" : "Mehr Werkzeuge"}
        data-testid="editor-toggle-toolbar"
      >
        <span className="text-xs">{expanded ? "Weniger" : "Mehr"}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </Button>

      {/* Sekundäre Werkzeuge: auf dem Handy einklappbar, auf dem Desktop immer sichtbar */}
      <div className={`${expanded ? "flex" : "hidden"} sm:flex flex-wrap items-center gap-1 w-full sm:w-auto`}>
      <div className="w-px h-8 bg-border mx-1 hidden sm:block" />

      {/* Colors */}
      <ColorPickerButton
        colors={TEXT_COLORS}
        onSelect={setTextColor}
        currentColor={editor.getAttributes('textStyle').color}
        icon={Palette}
        title="Textfarbe"
      />
      <ColorPickerButton
        colors={HIGHLIGHT_COLORS}
        onSelect={setHighlightColor}
        currentColor={editor.getAttributes('highlight').color || '#fef08a'}
        icon={Highlighter}
        title="Markierungsfarbe"
      />

      <div className="w-px h-8 bg-border mx-1" />

      {/* Alignment */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={`h-8 px-2 ${editor.isActive({ textAlign: 'left' }) ? 'bg-accent' : ''}`}
        title="Linksbündig"
      >
        <AlignLeft className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={`h-8 px-2 ${editor.isActive({ textAlign: 'center' }) ? 'bg-accent' : ''}`}
        title="Zentriert"
      >
        <AlignCenter className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={`h-8 px-2 ${editor.isActive({ textAlign: 'right' }) ? 'bg-accent' : ''}`}
        title="Rechtsbündig"
      >
        <AlignRight className="w-4 h-4" />
      </Button>

      <div className="w-px h-8 bg-border mx-1" />

      {/* Lists */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`h-8 px-2 ${editor.isActive('bulletList') ? 'bg-accent' : ''}`}
        title="Aufzählung"
      >
        <List className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`h-8 px-2 ${editor.isActive('orderedList') ? 'bg-accent' : ''}`}
        title="Nummerierte Liste"
      >
        <ListOrdered className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleTaskList().run()}
        className={`h-8 px-2 ${editor.isActive('taskList') ? 'bg-accent' : ''}`}
        title="Checkliste"
      >
        <CheckSquare className="w-4 h-4" />
      </Button>

      <div className="w-px h-8 bg-border mx-1" />

      {/* Table */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={`h-8 px-2 ${editor.isActive('table') ? 'bg-accent' : ''}`}
            title="Tabelle"
          >
            <TableIcon className="w-4 h-4" />
            <ChevronDown className="w-3 h-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
            <Plus className="w-4 h-4 mr-2" />
            Tabelle einfügen (3x3)
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => editor.chain().focus().addColumnAfter().run()} disabled={!editor.can().addColumnAfter()}>
            Spalte hinzufügen
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => editor.chain().focus().addRowAfter().run()} disabled={!editor.can().addRowAfter()}>
            Zeile hinzufügen
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => editor.chain().focus().deleteColumn().run()} disabled={!editor.can().deleteColumn()}>
            Spalte löschen
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => editor.chain().focus().deleteRow().run()} disabled={!editor.can().deleteRow()}>
            Zeile löschen
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => editor.chain().focus().deleteTable().run()} disabled={!editor.can().deleteTable()} className="text-red-600">
            <Trash2 className="w-4 h-4 mr-2" />
            Tabelle löschen
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="w-px h-8 bg-border mx-1" />

      {/* Special elements */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addLink}
        className={`h-8 px-2 ${editor.isActive('link') ? 'bg-accent' : ''}`}
        title="Link einfügen"
      >
        <LinkIcon className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addImage}
        className="h-8 px-2"
        title="Bild einfügen"
      >
        <ImageIcon className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={`h-8 px-2 ${editor.isActive('blockquote') ? 'bg-accent' : ''}`}
        title="Zitat"
      >
        <Quote className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={`h-8 px-2 ${editor.isActive('codeBlock') ? 'bg-accent' : ''}`}
        title="Code-Block"
      >
        <Code className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        className="h-8 px-2"
        title="Trennlinie"
      >
        <SeparatorHorizontal className="w-4 h-4" />
      </Button>

      <div className="w-px h-8 bg-border mx-1" />

      {/* Tools */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onAutoLink}
        className="h-8 px-2 text-primary hover:text-primary"
        title="URLs & E-Mails automatisch verlinken"
      >
        <Sparkles className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onCleanupHighlights}
        className="h-8 px-2 text-amber-600 hover:text-amber-700"
        title="Blaue Markierungen bereinigen"
      >
        <Eraser className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().unsetAllMarks().run()}
        className="h-8 px-2"
        title="Formatierung entfernen"
      >
        <RemoveFormatting className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className="h-8 px-2"
        title="Rückgängig"
      >
        <Undo className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className="h-8 px-2"
        title="Wiederholen"
      >
        <Redo className="w-4 h-4" />
      </Button>
      </div>

      {/* Character count */}
      {characterCount !== undefined && (
        <span className="ml-auto text-xs text-muted-foreground self-center px-2">
          {characterCount} Zeichen
        </span>
      )}
    </div>
  );
};
